import api from './api';
import { LoggerService } from './LoggerService';

export interface RiskMetric {
  id: string;
  name: string;
  value: number;
  change: number;
  threshold: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'market' | 'credit' | 'operational' | 'liquidity' | 'regulatory';
  description: string;
  lastUpdated: string;
}

export interface StressTestScenario {
  id: string;
  name: string;
  description: string;
  type: 'market_crash' | 'interest_rate' | 'credit_event' | 'liquidity_crisis' | 'regulatory_change';
  severity: 'mild' | 'moderate' | 'severe' | 'extreme';
  parameters: {
    marketShock?: number;
    interestRateChange?: number;
    creditSpreadChange?: number;
    liquidityImpact?: number;
    timeHorizon: number; // days
  };
  results?: StressTestResult;
}

export interface StressTestResult {
  portfolioImpact: number;
  varImpact: number;
  liquidityRatio: number;
  capitalAdequacy: number;
  timeToRecovery: number; // days
  worstCaseScenario: {
    loss: number;
    probability: number;
    description: string;
  };
  mitigationStrategies: string[];
}

export interface RiskHeatmapData {
  category: string;
  subcategory: string;
  riskLevel: number;
  impact: number;
  probability: number;
  mitigation: string;
  owner: string;
  dueDate?: string;
}

export interface MonteCarloSimulation {
  id: string;
  name: string;
  description: string;
  iterations: number;
  timeHorizon: number; // days
  confidenceLevel: number;
  results: {
    expectedReturn: number;
    var95: number;
    var99: number;
    expectedShortfall: number;
    volatility: number;
    sharpeRatio: number;
    maxDrawdown: number;
    distribution: { value: number; probability: number }[];
  };
  status: 'running' | 'completed' | 'failed';
  createdAt: string;
  completedAt?: string;
}

export interface BacktestingResult {
  id: string;
  strategy: string;
  startDate: string;
  endDate: string;
  totalReturn: number;
  annualizedReturn: number;
  volatility: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  profitFactor: number;
  performanceChart: { date: string; value: number; benchmark: number }[];
  trades: {
    date: string;
    action: 'buy' | 'sell';
    symbol: string;
    quantity: number;
    price: number;
    pnl: number;
  }[];
}

export interface RiskAlert {
  id: string;
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  category: string;
  timestamp: string;
  isRead: boolean;
  actionRequired: boolean;
  recommendation?: string;
  affectedEntities: string[];
}

class RiskAnalyticsService {
  async getRiskMetrics(): Promise<RiskMetric[]> {
    try {
      const response = await api.get('/risk-analytics/metrics');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching risk metrics:', error);
      throw error;
    }
  }

  async getStressTestScenarios(): Promise<StressTestScenario[]> {
    try {
      const response = await api.get('/risk-analytics/stress-test-scenarios');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching stress test scenarios:', error);
      throw error;
    }
  }

  async runStressTest(scenarioId: string): Promise<StressTestResult> {
    try {
      const response = await api.post(`/risk-analytics/stress-test/${scenarioId}/run`);
      return response.data;
    } catch (error) {
      LoggerService.error('Error running stress test:', error);
      throw error;
    }
  }

  async getRiskHeatmap(): Promise<RiskHeatmapData[]> {
    try {
      const response = await api.get('/risk-analytics/heatmap');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching risk heatmap:', error);
      throw error;
    }
  }

  async createMonteCarloSimulation(config: Partial<MonteCarloSimulation>): Promise<MonteCarloSimulation> {
    try {
      const response = await api.post('/risk-analytics/monte-carlo', config);
      return response.data;
    } catch (error) {
      LoggerService.error('Error creating Monte Carlo simulation:', error);
      throw error;
    }
  }

  async getBacktestingResults(): Promise<BacktestingResult[]> {
    try {
      const response = await api.get('/risk-analytics/backtesting');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching backtesting results:', error);
      throw error;
    }
  }

  async getRiskAlerts(): Promise<RiskAlert[]> {
    try {
      const response = await api.get('/risk-analytics/alerts');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching risk alerts:', error);
      throw error;
    }
  }
}

export const riskAnalyticsService = new RiskAnalyticsService();